import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { TransactionStatus } from 'shared/constants';
import { Transaction } from 'shared/entities';
import EarningsChart from './components/EarningsChart';
import EarningsStatCard from './components/EarningsStatCard';
import MonthlyBreakdown from './components/MonthlyBreakdown';
import PayoutInfo from './components/PayoutInfo';
import TransactionHistory from './components/TransactionHistory';

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterEarnings: React.FC = () => {
  const stats = {
    availableBalance: 1245.0,
    thisMonth: 840.0,
    thisMonthChange: '+15%',
    pending: 420.0,
    pendingBookings: 3,
    totalEarned: 12450.0,
    sinceDate: 'Jan 2025',
  };

  const payoutInfo = {
    bankName: 'Chase Bank',
    accountNumber: '4242',
    schedule: 'Weekly (Fridays)',
    nextPayoutDate: '2025-06-14',
    nextPayoutAmount: 1245.0,
    processingTime: '2-3 business days',
  };

  const monthlyBreakdown = {
    totalBookings: 8,
    grossEarnings: 880.0,
    serviceFee: 40.0,
    serviceFeePercent: 5,
    netEarnings: 840.0,
    averagePerBooking: 105.0,
  };

  const [transactions] = useState<Transaction[]>([
    {
      id: '1',
      clientName: 'Michael B.',
      clientInitials: 'MB',
      petName: 'Luna',
      serviceType: 'Boarding',
      duration: '3 days',
      date: '2025-06-10',
      amount: 120.0,
      status: TransactionStatus.COMPLETED,
      payoutDate: '2025-06-14',
    },
    {
      id: '2',
      clientName: 'Jennifer L.',
      clientInitials: 'JL',
      petName: 'Max',
      serviceType: 'Drop-in visits',
      duration: '2 days',
      date: '2025-06-08',
      amount: 80.0,
      status: TransactionStatus.COMPLETED,
      payoutDate: '2025-06-14',
    },
    {
      id: '3',
      clientName: 'Sarah M.',
      clientInitials: 'SM',
      petName: '2 Dogs',
      serviceType: 'House visits',
      duration: '3 days',
      date: '2025-06-15',
      amount: 180.0,
      status: TransactionStatus.PENDING,
      payoutDate: '2025-06-21',
    },
    {
      id: '4',
      clientName: 'Amanda K.',
      clientInitials: 'AK',
      petName: 'Coco',
      serviceType: 'Boarding',
      duration: '2 days',
      date: '2025-06-05',
      amount: 150.0,
      status: TransactionStatus.PROCESSING,
      payoutDate: '2025-06-14',
    },
    {
      id: '5',
      clientName: 'John D.',
      clientInitials: 'JD',
      petName: 'Luna',
      serviceType: 'Drop-in visits',
      duration: '5 days',
      date: '2025-06-01',
      amount: 200.0,
      status: TransactionStatus.COMPLETED,
      payoutDate: '2025-06-07',
    },
    {
      id: '6',
      clientName: 'Emma R.',
      clientInitials: 'ER',
      petName: 'Charlie',
      serviceType: 'Boarding',
      duration: '7 days',
      date: '2025-05-28',
      amount: 420.0,
      status: TransactionStatus.COMPLETED,
      payoutDate: '2025-06-07',
    },
  ] as unknown as Transaction[]);

  const handleExport = () => {
    console.log('Export earnings data');
    // Future: implement CSV/PDF export
  };

  const handleWithdraw = () => {
    console.log('Initiate withdrawal');
    // Future: open withdrawal modal/flow
  };

  const handleEditPayout = () => {
    console.log('Edit payout method');
    // Future: open payout settings modal
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />
      <Box sx={{ position: 'relative' }}>
      {/* Blur overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(255,255,255,0.6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <LockIcon sx={{ fontSize: 56, color: '#178B8E' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
          Premium Feature
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', maxWidth: 320 }}
        >
          Earnings and payouts are available for premium sitters. Upgrade your plan to unlock this
          feature.
        </Typography>
        <Button
          variant="contained"
          size="large"
          sx={{
            backgroundColor: '#178B8E',
            borderRadius: 25,
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            '&:hover': { backgroundColor: '#0F6164' },
          }}
        >
          Upgrade to Premium
        </Button>
      </Box>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Earnings
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{
                borderRadius: 25,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
              onClick={handleWithdraw}
              sx={{
                borderRadius: 25,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                backgroundColor: '#178B8E',
                '&:hover': {
                  backgroundColor: '#0F6164',
                },
              }}
            >
              Withdraw
            </Button>
          </Box>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EarningsStatCard
              title="Available Balance"
              value={`$${stats.availableBalance.toFixed(2)}`}
              subtitle="Ready to withdraw"
              icon={<AccountBalanceWalletIcon sx={{ color: 'white' }} />}
              highlighted={true}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EarningsStatCard
              title="This Month"
              value={`$${stats.thisMonth.toFixed(2)}`}
              subtitle={`↑ ${stats.thisMonthChange} vs last month`}
              icon={<CalendarTodayIcon sx={{ color: '#178B8E', fontSize: 20 }} />}
              valueColor="#178B8E"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EarningsStatCard
              title="Pending"
              value={`$${stats.pending.toFixed(2)}`}
              subtitle={`From ${stats.pendingBookings} bookings`}
              icon={<HourglassEmptyIcon sx={{ color: '#FFA726', fontSize: 20 }} />}
              valueColor="#FFA726"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EarningsStatCard
              title="Total Earned"
              value={`$${stats.totalEarned.toLocaleString()}`}
              subtitle={`Since ${stats.sinceDate}`}
              icon={<EmojiEventsIcon sx={{ color: '#FDB833', fontSize: 20 }} />}
              valueColor="#178B8E"
            />
          </Grid>
        </Grid>

        {/* Earnings Chart */}
        <EarningsChart />

        {/* Payout Info & Monthly Breakdown */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <PayoutInfo
              bankName={payoutInfo.bankName}
              accountNumber={payoutInfo.accountNumber}
              schedule={payoutInfo.schedule}
              nextPayoutDate={payoutInfo.nextPayoutDate}
              nextPayoutAmount={payoutInfo.nextPayoutAmount}
              processingTime={payoutInfo.processingTime}
              onEdit={handleEditPayout}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <MonthlyBreakdown
              totalBookings={monthlyBreakdown.totalBookings}
              grossEarnings={monthlyBreakdown.grossEarnings}
              serviceFee={monthlyBreakdown.serviceFee}
              serviceFeePercent={monthlyBreakdown.serviceFeePercent}
              netEarnings={monthlyBreakdown.netEarnings}
              averagePerBooking={monthlyBreakdown.averagePerBooking}
            />
          </Grid>
        </Grid>

        {/* Transaction History */}
        <TransactionHistory transactions={transactions} />
      </Container>
      </Box>
    </Box>
  );
};

export default SitterEarnings;
